import { useCallback } from "react";
import i18n from "@shared/i18n/i18n";
import {
  Event,
  Instructor,
  Venue,
  Brand,
  MultilingualText,
  AgendaDay,
} from "@features/events/types";

/**
 * Entity types supported for AI context generation
 */
export enum AIContextEntityType {
  EVENT = "event",
  INSTRUCTOR = "instructor",
  VENUE = "venue",
  BRAND = "brand",
}

/**
 * Data structure for building AI context
 * All properties are optional - the hook will use what's available
 */
export interface AIContextData {
  event?: Partial<Event>;
  instructors?: Partial<Instructor>[];
  venues?: Partial<Venue>[];
  instructor?: Partial<Instructor>;
  venue?: Partial<Venue>;
  brand?: Partial<Brand>;
}

/**
 * Hook for building AI-optimized context strings from entity data.
 * Designed for AI image description generation.
 */
export function useAIContext() {
  /**
   * Get localized value from a MultilingualText object
   */
  const getLocalizedValue = useCallback(
    (obj: MultilingualText | undefined | null): string => {
      if (!obj) return "";
      const currentLang = i18n.language;
      return obj[currentLang] || Object.values(obj).filter(Boolean)[0] || "";
    },
    []
  );

  /**
   * Strip HTML tags from text
   */
  const stripHtml = (html: string): string => {
    return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  };

  /**
   * Truncate text to max length, ending at word boundary
   */
  const truncate = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) return text;
    const truncated = text.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(" ");
    return lastSpace > maxLength * 0.7
      ? truncated.substring(0, lastSpace)
      : truncated;
  };

  /**
   * Extract session titles and descriptions from agenda (skip swimlane/room names)
   */
  const extractSessionDetails = useCallback(
    (agenda: AgendaDay[]): string => {
      const sessions: string[] = [];
      const seenTitles = new Set<string>();

      for (const day of agenda) {
        for (const swimlane of day.swimlines || []) {
          // Skip swimlane titles (usually room names)
          for (const session of swimlane.sessions || []) {
            const title = getLocalizedValue(session.title);
            if (title && !seenTitles.has(title)) {
              seenTitles.add(title);

              const description = session.description
                ? stripHtml(getLocalizedValue(session.description))
                : "";

              if (description) {
                sessions.push(`- ${title}: ${truncate(description, 150)}`);
              } else {
                sessions.push(`- ${title}`);
              }
            }
            if (sessions.length >= 5) break;
          }
          if (sessions.length >= 5) break;
        }
        if (sessions.length >= 5) break;
      }

      return sessions.join("\n");
    },
    [getLocalizedValue]
  );

  /**
   * Build AI context for an Event
   */
  const buildEventContext = useCallback(
    (
      event: Partial<Event>,
      instructors?: Partial<Instructor>[],
      venues?: Partial<Venue>[]
    ): string => {
      const parts: string[] = [];

      // Event title - clearly labeled
      const title = getLocalizedValue(event.title);
      if (title) {
        parts.push(`Event title : ${title}`);
      }

      // Event description
      const description = stripHtml(getLocalizedValue(event.description));
      if (description) {
        parts.push(`Event description: ${truncate(description, 400)}`);
      }

      // Instructors - detailed information
      if (instructors && instructors.length > 0) {
        const instructorDetails = instructors.slice(0, 5).map((inst) => {
          const detailParts: string[] = [];
          
          // Name with title
          const firstName = getLocalizedValue(inst.firstname);
          const lastName = getLocalizedValue(inst.lastname);
          const fullName = `${firstName} ${lastName}`.trim();
          const nameWithTitle = inst.title ? `${inst.title} ${fullName}` : fullName;
          if (nameWithTitle) {
            detailParts.push(nameWithTitle);
          }
          
          // Headline
          const headline = getLocalizedValue(inst.headline);
          if (headline) {
            detailParts.push(`Instructor role: ${headline}`);
          }
          
          // Bio
          const bio = stripHtml(getLocalizedValue(inst.bio));
          if (bio) {
            detailParts.push(`Bio: ${truncate(bio, 200)}`);
          }
          
          return detailParts.join("; ");
        }).filter(Boolean);
        
        if (instructorDetails.length) {
          parts.push(`Instructors:\n${instructorDetails.map(d => `- ${d}`).join("\n")}`);
        }
      }

      // Venue - name, city, and country
      if (venues && venues.length > 0) {
        const venueDetails = venues.slice(0, 3).map((venue) => {
          const name = getLocalizedValue(venue.name);
          const city = venue.geoAddress
            ? getLocalizedValue(venue.geoAddress.city)
            : "";
          const country = venue.geoAddress
            ? getLocalizedValue(venue.geoAddress.country)
            : "";
          
          const locationParts = [name, city, country].filter(Boolean);
          return locationParts.join(", ");
        }).filter(Boolean);
        
        if (venueDetails.length) {
          parts.push(`Venue: ${venueDetails.join("; ")}`);
        }
      }

      // Agenda - extract session titles and descriptions
      if (event.agenda?.length) {
        const sessions = extractSessionDetails(event.agenda);
        if (sessions) {
          parts.push(`Program sessions:\n${sessions}`);
        }
      }

      return parts.join("\n\n");
    },
    [getLocalizedValue, extractSessionDetails]
  );

  /**
   * Build AI context for an Instructor
   */
  const buildInstructorContext = useCallback(
    (instructor: Partial<Instructor>): string => {
      const parts: string[] = [];

      // Name with title
      const name = `${getLocalizedValue(instructor.firstname)} ${getLocalizedValue(instructor.lastname)}`.trim();
      const title = instructor.title || "";
      if (name) {
        parts.push(`Person: ${title ? `${title} ` : ""}${name}`);
      }

      // Professional headline
      const headline = getLocalizedValue(instructor.headline);
      if (headline) {
        parts.push(`Role: ${headline}`);
      }

      // Position
      if (instructor.currentPosition || instructor.currentCompany) {
        const pos = [instructor.currentPosition, instructor.currentCompany]
          .filter(Boolean)
          .join(" at ");
        if (pos) {
          parts.push(`Position: ${pos}`);
        }
      }

      // Bio
      const bio = stripHtml(getLocalizedValue(instructor.bio));
      if (bio) {
        parts.push(`Background: ${truncate(bio, 300)}`);
      }

      return parts.join("\n");
    },
    [getLocalizedValue]
  );

  /**
   * Build AI context for a Venue
   */
  const buildVenueContext = useCallback(
    (venue: Partial<Venue>): string => {
      const parts: string[] = [];

      // Name
      const name = getLocalizedValue(venue.name);
      if (name) {
        parts.push(`Venue: ${name}`);
      }

      // Location
      if (venue.geoAddress) {
        const location = getLocalizedValue(venue.geoAddress.summary);
        if (location) {
          parts.push(`Location: ${location}`);
        }
      }

      // Description
      const description = stripHtml(getLocalizedValue(venue.description));
      if (description) {
        parts.push(`About: ${truncate(description, 300)}`);
      }

      // Capacity
      if (venue.maxCapacity) {
        parts.push(`Capacity: ${venue.maxCapacity} people`);
      }

      return parts.join("\n");
    },
    [getLocalizedValue]
  );

  /**
   * Build AI context for a Brand
   */
  const buildBrandContext = useCallback(
    (brand: Partial<Brand>): string => {
      const parts: string[] = [];

      // Name
      const name = getLocalizedValue(brand.name);
      if (name) {
        parts.push(`Brand: ${name}`);
      }

      // Tagline
      const headline = getLocalizedValue(brand.headline);
      if (headline) {
        parts.push(`Tagline: ${headline}`);
      }

      // Description
      const description = stripHtml(getLocalizedValue(brand.description));
      if (description) {
        parts.push(`About: ${truncate(description, 300)}`);
      }

      return parts.join("\n");
    },
    [getLocalizedValue]
  );

  /**
   * Main function to build context based on entity type
   */
  const buildContext = useCallback(
    (entityType: AIContextEntityType, data: AIContextData): string => {
      // DEBUG: Log the incoming data for verification
      console.log("=== AI Context Debug ===");
      console.log("Entity Type:", entityType);
      console.log("Full Data Object:", JSON.stringify(data, null, 2));
      console.log("Event:", data.event);
      console.log("Instructors:", data.instructors);
      console.log("Venues:", data.venues);
      console.log("Instructor (single):", data.instructor);
      console.log("Venue (single):", data.venue);
      console.log("Brand:", data.brand);
      console.log("========================");

      let result = "";
      switch (entityType) {
        case AIContextEntityType.EVENT:
          if (data.event) {
            result = buildEventContext(data.event, data.instructors, data.venues);
          }
          break;
        case AIContextEntityType.INSTRUCTOR:
          if (data.instructor) {
            result = buildInstructorContext(data.instructor);
          }
          break;
        case AIContextEntityType.VENUE:
          if (data.venue) {
            result = buildVenueContext(data.venue);
          }
          break;
        case AIContextEntityType.BRAND:
          if (data.brand) {
            result = buildBrandContext(data.brand);
          }
          break;
      }

      console.log("=== Generated Context ===");
      console.log(result);
      console.log("=========================");
      return result;
    },
    [
      buildEventContext,
      buildInstructorContext,
      buildVenueContext,
      buildBrandContext,
    ]
  );

  return {
    buildContext,
    buildEventContext,
    buildInstructorContext,
    buildVenueContext,
    buildBrandContext,
    getLocalizedValue,
  };
}

export default useAIContext;
